import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage, useTheme, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import LogoUploader from "../components/LogoUploader";
import { 
  Sun, 
  Moon, 
  Globe, 
  ArrowLeft,
  Palette,
  Check,
  Building2,
  MapPin,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const RegisterSalon = () => {
  const { t, language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  // Get plan from URL or default to mono_sede
  const initialPlanType = searchParams.get("plan") === "multi_sede" ? "multi_sede" : "mono_sede";
  
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    owner_email: "",
    owner_name: "",
    owner_password: "",
    owner_password_confirm: "",
    phone: "",
    address: "",
    logo_url: "",
    max_concurrent_bookings: 1,
    plan_type: initialPlanType, // "mono_sede" or "multi_sede"
    billing_period: "monthly", // "monthly" or "annual"
    branding: {
      primary_color: "#6366f1",
      secondary_color: "#8b5cf6",
      accent_color: "#ec4899"
    },
    // GDPR consent
    terms_accepted: false,
    privacy_accepted: false
  });

  // Plan details
  const plans = {
    mono_sede: {
      name: "SmartBooker Pro",
      monthly_price: 34.95,
      yearly_price: 349,
      trial_days: 14,
      max_locations: 1
    },
    multi_sede: {
      name: "SmartBooker Business",
      monthly_price: 59.00,
      yearly_price: 590,
      trial_days: 30,
      max_locations: 10
    }
  };

  const currentPlan = plans[formData.plan_type];

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({ 
      ...formData, 
      name,
      slug: generateSlug(name)
    });
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) {
      toast.error(language === "it" ? "Inserisci il nome dell'attività" : "Enter business name");
      return false;
    }
    if (!formData.slug.trim()) {
      toast.error(language === "it" ? "Inserisci lo slug URL" : "Enter URL slug");
      return false;
    }
    if (!formData.owner_name.trim()) {
      toast.error(language === "it" ? "Inserisci il tuo nome" : "Enter your name");
      return false;
    }
    if (!formData.owner_email.trim()) {
      toast.error(language === "it" ? "Inserisci la tua email" : "Enter your email");
      return false;
    }
    if (!formData.owner_password || formData.owner_password.length < 6) {
      toast.error(language === "it" ? "La password deve avere almeno 6 caratteri" : "Password must be at least 6 characters");
      return false;
    }
    if (formData.owner_password !== formData.owner_password_confirm) {
      toast.error(language === "it" ? "Le password non coincidono" : "Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep1()) return;
    
    // Validate GDPR consent
    if (!formData.terms_accepted || !formData.privacy_accepted) {
      toast.error(
        language === "it" 
          ? "Devi accettare i Termini e Condizioni e la Privacy Policy per procedere" 
          : "You must accept the Terms and Conditions and Privacy Policy to continue"
      );
      return;
    }
    
    setLoading(true);
    
    try {
      // Register salon via public endpoint
      const response = await axios.post(`${API}/public/register-salon`, {
        name: formData.name,
        slug: formData.slug,
        owner_email: formData.owner_email,
        owner_name: formData.owner_name,
        owner_password: formData.owner_password,
        phone: formData.phone,
        address: formData.address,
        logo_url: formData.logo_url,
        max_concurrent_bookings: formData.max_concurrent_bookings,
        plan_type: formData.plan_type, // "mono_sede" or "multi_sede"
        billing_period: formData.billing_period, // "monthly" or "annual"
        branding: formData.branding,
        // GDPR consent data
        terms_accepted: formData.terms_accepted,
        privacy_accepted: formData.privacy_accepted,
        terms_version: "v1.0_2026-03-13",
        privacy_version: "v1.0_2026-03-13"
      });
      
      toast.success(
        language === "it" 
          ? "Registrazione completata! Ora puoi accedere." 
          : "Registration complete! You can now login."
      );
      
      // Redirect to login with email pre-filled
      navigate("/login", { state: { email: formData.owner_email } });
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 
        (language === "it" ? "Errore durante la registrazione" : "Registration error");
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
                alt="SmartBooker" 
                className="h-8 w-auto"
              />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => changeLanguage(language === "it" ? "en" : "it")}
                className="p-2 rounded-full hover:bg-muted transition-colors"
                data-testid="language-toggle"
              >
                <Globe className="w-5 h-5" />
              </button>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-muted transition-colors"
                data-testid="theme-toggle"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>

              <Link to="/login">
                <Button variant="outline" className="rounded-full" data-testid="login-btn">
                  {t("login")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === "it" ? "Torna alla home" : "Back to home"}
          </Link>

          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-3xl font-serif font-medium">
              {language === "it" ? "Registra la tua Attività" : "Register your Business"}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {language === "it" 
                ? `Inizia i tuoi ${currentPlan.trial_days} giorni di prova gratuita` 
                : `Start your ${currentPlan.trial_days}-day free trial`
              }
            </p>
          </div>

          {/* Plan Selection */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, plan_type: "mono_sede" })}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                formData.plan_type === "mono_sede" 
                  ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20" 
                  : "border-border hover:border-violet-300"
              }`}
              data-testid="plan-mono-sede"
            >
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-violet-500" />
                <span className="font-medium text-sm">
                  {language === "it" ? "Mono Sede" : "Single Location"}
                </span>
              </div>
              <p className="text-xl font-bold">€34,95<span className="text-sm font-normal text-muted-foreground">/mese</span></p>
              <p className="text-xs text-muted-foreground mt-1">
                {language === "it" ? "14 giorni gratis" : "14 days free"}
              </p>
            </button>
            
            <button
              type="button"
              onClick={() => setFormData({ ...formData, plan_type: "multi_sede" })}
              className={`p-4 rounded-xl border-2 text-left transition-all relative ${
                formData.plan_type === "multi_sede" 
                  ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20" 
                  : "border-border hover:border-violet-300"
              }`}
              data-testid="plan-multi-sede"
            >
              <div className="absolute -top-2 -right-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs font-medium">
                  <Sparkles className="w-3 h-3" />
                  {language === "it" ? "Popolare" : "Popular"}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-purple-500" />
                <span className="font-medium text-sm">
                  {language === "it" ? "Multi Sede" : "Multi Location"}
                </span>
              </div>
              <p className="text-xl font-bold">€59,00<span className="text-sm font-normal text-muted-foreground">/mese</span></p>
              <p className="text-xs text-muted-foreground mt-1">
                {language === "it" ? "30 giorni gratis • Fino a 10 sedi" : "30 days free • Up to 10 locations"}
              </p>
            </button>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-accent' : 'bg-muted'}`}></div>
            <div className={`w-16 h-0.5 ${step >= 2 ? 'bg-accent' : 'bg-muted'}`}></div>
            <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-accent' : 'bg-muted'}`}></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div className="space-y-6 animate-in fade-in">
                <div className="p-6 rounded-2xl border bg-card">
                  <h3 className="font-semibold mb-4">
                    {language === "it" ? "Informazioni Attività" : "Business Information"}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">
                        {language === "it" ? "Nome Attività" : "Business Name"} *
                      </label>
                      <Input
                        value={formData.name}
                        onChange={handleNameChange}
                        placeholder="Hair Studio Milano"
                        required
                        data-testid="salon-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1.5">
                        Slug (URL) *
                      </label>
                      <Input
                        value={formData.slug}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") 
                        })}
                        placeholder="hair-studio-milano"
                        required
                        data-testid="salon-slug-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {language === "it" ? "La tua pagina prenotazioni:" : "Your booking page:"} /book/{formData.slug || "your-slug"}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1.5">
                          {language === "it" ? "Telefono" : "Phone"}
                        </label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+39 02 1234567"
                          data-testid="salon-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5">
                          {language === "it" ? "Indirizzo" : "Address"}
                        </label>
                        <Input
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          placeholder="Via Roma 123, Milano"
                          data-testid="salon-address-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl border bg-card">
                  <h3 className="font-semibold mb-4">
                    {language === "it" ? "Account Proprietario" : "Owner Account"}
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1.5">
                          {language === "it" ? "Il tuo Nome" : "Your Name"} *
                        </label>
                        <Input
                          value={formData.owner_name}
                          onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                          required
                          data-testid="owner-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5">
                          {language === "it" ? "La tua Email" : "Your Email"} *
                        </label>
                        <Input
                          type="email"
                          value={formData.owner_email}
                          onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                          required
                          data-testid="owner-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1.5">
                          Password *
                        </label>
                        <Input
                          type="password"
                          value={formData.owner_password}
                          onChange={(e) => setFormData({ ...formData, owner_password: e.target.value })}
                          placeholder={language === "it" ? "Minimo 6 caratteri" : "Minimum 6 characters"}
                          required
                          data-testid="owner-password-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5">
                          {language === "it" ? "Conferma Password" : "Confirm Password"} *
                        </label>
                        <Input
                          type="password"
                          value={formData.owner_password_confirm}
                          onChange={(e) => setFormData({ ...formData, owner_password_confirm: e.target.value })}
                          required
                          data-testid="owner-password-confirm-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button 
                    type="button" 
                    onClick={() => {
                      if (validateStep1()) setStep(2);
                    }}
                    className="rounded-full"
                    data-testid="next-step-btn"
                  >
                    {language === "it" ? "Continua" : "Continue"}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Customization */}
            {step === 2 && (
              <div className="space-y-6 animate-in fade-in">
                <div className="p-6 rounded-2xl border bg-card">
                  <h3 className="font-semibold mb-4">
                    {language === "it" ? "Configurazione" : "Configuration"}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">
                        {language === "it" ? "Numero Postazioni / Poltrone" : "Number of Stations / Chairs"}
                      </label>
                      <p className="text-xs text-muted-foreground mb-2">
                        {language === "it" 
                          ? "Quanti appuntamenti possono essere gestiti contemporaneamente" 
                          : "How many appointments can be handled at the same time"}
                      </p>
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={formData.max_concurrent_bookings}
                        onChange={(e) => setFormData({ ...formData, max_concurrent_bookings: parseInt(e.target.value) || 1 })}
                        className="w-24"
                        data-testid="stations-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1.5">
                        {language === "it" ? "Periodo di Fatturazione" : "Billing Period"}
                      </label>
                      <Select
                        value={formData.billing_period}
                        onValueChange={(value) => setFormData({ ...formData, billing_period: value })}
                      >
                        <SelectTrigger data-testid="billing-period-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">
                            {language === "it" 
                              ? `Mensile - €${currentPlan.monthly_price.toFixed(2).replace('.', ',')}/mese` 
                              : `Monthly - €${currentPlan.monthly_price.toFixed(2)}/month`
                            }
                          </SelectItem>
                          <SelectItem value="annual">
                            {language === "it" 
                              ? `Annuale - €${currentPlan.yearly_price}/anno (2 mesi gratis)` 
                              : `Annual - €${currentPlan.yearly_price}/year (2 months free)`
                            }
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        {language === "it" 
                          ? `I primi ${currentPlan.trial_days} giorni sono gratuiti, indipendentemente dal periodo scelto` 
                          : `First ${currentPlan.trial_days} days are free, regardless of the billing period chosen`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-2xl border bg-card">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    {language === "it" ? "Personalizzazione (Opzionale)" : "Customization (Optional)"}
                  </h3>
                  
                  <div className="space-y-4">
                    <LogoUploader
                      value={formData.logo_url}
                      onChange={(url) => setFormData({ ...formData, logo_url: url })}
                      language={language}
                    />

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1.5">
                          {language === "it" ? "Colore Primario" : "Primary Color"}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.branding.primary_color}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              branding: { ...formData.branding, primary_color: e.target.value }
                            })}
                            className="w-10 h-10 rounded cursor-pointer border"
                          />
                          <Input
                            value={formData.branding.primary_color}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              branding: { ...formData.branding, primary_color: e.target.value }
                            })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1.5">
                          {language === "it" ? "Colore Secondario" : "Secondary Color"}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.branding.secondary_color}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              branding: { ...formData.branding, secondary_color: e.target.value }
                            })}
                            className="w-10 h-10 rounded cursor-pointer border"
                          />
                          <Input
                            value={formData.branding.secondary_color}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              branding: { ...formData.branding, secondary_color: e.target.value }
                            })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1.5">
                          {language === "it" ? "Colore Accento" : "Accent Color"}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.branding.accent_color}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              branding: { ...formData.branding, accent_color: e.target.value }
                            })}
                            className="w-10 h-10 rounded cursor-pointer border"
                          />
                          <Input
                            value={formData.branding.accent_color}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              branding: { ...formData.branding, accent_color: e.target.value }
                            })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="p-4 rounded-lg border bg-muted/30">
                      <p className="text-xs text-muted-foreground mb-2">
                        {language === "it" ? "Anteprima:" : "Preview:"}
                      </p>
                      <div className="flex items-center gap-3">
                        {formData.logo_url ? (
                          <img 
                            src={formData.logo_url} 
                            alt="Logo preview" 
                            className="h-8 w-auto"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        ) : (
                          <div 
                            className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold"
                            style={{ backgroundColor: formData.branding.primary_color }}
                          >
                            {formData.name?.charAt(0) || "S"}
                          </div>
                        )}
                        <span className="font-semibold">{formData.name || "Nome Attività"}</span>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <div 
                          className="px-4 py-2 rounded text-white text-sm"
                          style={{ backgroundColor: formData.branding.primary_color }}
                        >
                          {language === "it" ? "Primario" : "Primary"}
                        </div>
                        <div 
                          className="px-4 py-2 rounded text-white text-sm"
                          style={{ backgroundColor: formData.branding.secondary_color }}
                        >
                          {language === "it" ? "Secondario" : "Secondary"}
                        </div>
                        <div 
                          className="px-4 py-2 rounded text-white text-sm"
                          style={{ backgroundColor: formData.branding.accent_color }}
                        >
                          {language === "it" ? "Accento" : "Accent"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trial info */}
                <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-emerald-900 dark:text-emerald-100">
                        {language === "it" 
                          ? `${currentPlan.trial_days} giorni di prova gratuita` 
                          : `${currentPlan.trial_days} days free trial`
                        }
                      </p>
                      <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
                        {language === "it" 
                          ? "Nessuna carta di credito richiesta. Potrai decidere di sottoscrivere un abbonamento al termine del periodo di prova."
                          : "No credit card required. You can decide to subscribe at the end of the trial period."
                        }
                      </p>
                      {formData.plan_type === "multi_sede" && (
                        <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-2 font-medium">
                          {language === "it" 
                            ? "✨ Piano Multi Sede: gestisci fino a 10 sedi con un unico account!"
                            : "✨ Multi Location Plan: manage up to 10 locations with one account!"
                          }
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* GDPR Consent Checkbox */}
                <div className="p-4 rounded-lg border bg-card">
                  <label 
                    className="flex items-start gap-3 cursor-pointer"
                    data-testid="terms-checkbox-label"
                  >
                    <input
                      type="checkbox"
                      checked={formData.terms_accepted && formData.privacy_accepted}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        terms_accepted: e.target.checked,
                        privacy_accepted: e.target.checked
                      })}
                      className="mt-1 w-5 h-5 rounded border-2 border-muted-foreground/50 text-accent focus:ring-accent cursor-pointer"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-sm">
                      {language === "it" ? (
                        <>
                          Ho letto e accetto i{" "}
                          <Link 
                            to="/terms-conditions" 
                            target="_blank" 
                            className="text-accent hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Termini e Condizioni
                          </Link>
                          {" "}e la{" "}
                          <Link 
                            to="/privacy-policy" 
                            target="_blank" 
                            className="text-accent hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Privacy Policy
                          </Link>
                          {" "}*
                        </>
                      ) : (
                        <>
                          I have read and accept the{" "}
                          <Link 
                            to="/terms-conditions" 
                            target="_blank" 
                            className="text-accent hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Terms and Conditions
                          </Link>
                          {" "}and the{" "}
                          <Link 
                            to="/privacy-policy" 
                            target="_blank" 
                            className="text-accent hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Privacy Policy
                          </Link>
                          {" "}*
                        </>
                      )}
                    </span>
                  </label>
                </div>

                <div className="flex justify-between">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="rounded-full"
                    data-testid="back-step-btn"
                  >
                    {language === "it" ? "Indietro" : "Back"}
                  </Button>
                  <Button 
                    type="submit" 
                    className="rounded-full btn-lift"
                    disabled={loading}
                    data-testid="register-salon-btn"
                  >
                    {loading 
                      ? (language === "it" ? "Registrazione..." : "Registering...") 
                      : (language === "it" ? "Registra Attività" : "Register Business")
                    }
                  </Button>
                </div>
              </div>
            )}
          </form>

          {/* Login link */}
          <p className="text-center text-sm text-muted-foreground mt-8">
            {language === "it" ? "Hai già un account?" : "Already have an account?"}{" "}
            <Link to="/login" className="text-accent hover:underline">
              {t("login")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterSalon;
